import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchData } from '../../api';
import { useDebugLogs } from '../../hooks/useDebugLogs';
import { useSystemData } from '../../hooks/useSystemData';
import { ControlDebugPanel } from '../ControlDebugPanel';
import { ComsConsolePanel } from '../ComsConsolePanel';

type TabType = 'devices' | 'coms' | 'control';

interface DeviceData {
  [key: string]: any;
}

interface DeviceInfo {
  type: 'meter' | 'inverter' | 'hybrid';
  model?: string;
  ratedPower?: number;
  sleeping?: boolean;
  data: DeviceData;
}

interface PortData {
  [deviceIndex: string]: DeviceInfo;
}

interface AllPortsData {
  [portId: string]: PortData;
}

interface PortStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  avgResponseTime: number;
  lastResponseTime: number;
  packetLossPercent: number;
}

export function AdvancedDevices() {
  const [activeTab, setActiveTab] = useState<TabType>('devices');
  const [portsData, setPortsData] = useState<AllPortsData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<{ portId: string; deviceIndex: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState<number>(0);
  const [isPaused, setIsPaused] = useState(false);

  const { data: debugData } = useDebugLogs(true, 2000);
  const { data: systemData } = useSystemData();

  // Fetch pause status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const result = await fetchData('/status');
        setIsPaused(result.isPaused || false);
      } catch (err) {
        console.error('Failed to fetch status:', err);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const togglePause = async () => {
    try {
      const response = await fetch('http://localhost:4000/togglePause', { method: 'POST' });
      const data = await response.json();
      setIsPaused(data.isPaused);
    } catch (err) {
      console.error('Failed to toggle pause:', err);
    }
  };

  // Update time since last update every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeSinceUpdate(Math.floor((Date.now() - lastUpdate) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdate]);

  const fetchAllData = useCallback(async () => {
    try {
      const result = await fetchData('/deviceData');
      setPortsData(result);
      setLastUpdate(Date.now());
    } catch (err) {
      console.error('Failed to fetch device data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePort = async (portId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete port ${portId} and all its devices?`)) return;

    try {
      const response = await fetch(`http://localhost:4000/port/${encodeURIComponent(portId)}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        // Clear selection if deleted port was selected
        if (selectedPort === portId) setSelectedPort(null);
        if (selectedDevice?.portId === portId) setSelectedDevice(null);
        fetchAllData();
      } else {
        const data = await response.json();
        alert(`Failed to delete: ${data.message}`);
      }
    } catch (err) {
      console.error('Failed to delete port:', err);
      alert('Failed to delete port');
    }
  };

  const deleteDevice = async (portId: string, deviceIndex: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete device #${deviceIndex} from ${portId}?`)) return;

    try {
      const response = await fetch(`http://localhost:4000/port/${encodeURIComponent(portId)}/device/${deviceIndex}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        // Clear selection if deleted device was selected
        if (selectedDevice?.portId === portId && selectedDevice?.deviceIndex === deviceIndex) {
          setSelectedDevice(null);
        }
        fetchAllData();
      } else {
        const data = await response.json();
        alert(`Failed to delete: ${data.message}`);
      }
    } catch (err) {
      console.error('Failed to delete device:', err);
      alert('Failed to delete device');
    }
  };

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 1000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  const formatTimestamp = (ts: number) => {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-GB', { hour12: false });
  };

  const formatTimeSince = (seconds: number) => {
    if (seconds < 1) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s ago`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m ago`;
  };

  // Get control limit percentage (0-100)
  const controlLimitPercent = systemData?.currentRateLimit
    ? (systemData.currentRateLimit / 100)
    : 100;

  const getPortType = (portId: string, portData: PortData): string => {
    const firstDevice = Object.values(portData)[0];
    return firstDevice?.type || 'unknown';
  };

  const getPortStats = (portId: string): PortStats | null => {
    return debugData?.ports[portId]?.stats || null;
  };

  const getDeviceCount = (portData: PortData): number => {
    return Object.keys(portData).length;
  };

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('voltage')) return `${value.toFixed(1)} V`;
      if (key.toLowerCase().includes('current')) return `${value.toFixed(2)} A`;
      if (key.toLowerCase().includes('power')) return `${Math.round(value)} W`;
      if (key.toLowerCase().includes('soc') || key.toLowerCase().includes('charge')) return `${value}%`;
      if (key.toLowerCase().includes('frequency')) return `${(value / 100).toFixed(2)} Hz`;
      return value.toFixed(2);
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const renderPortCard = (portId: string, portData: PortData) => {
    const portType = getPortType(portId, portData);
    const deviceCount = getDeviceCount(portData);
    const stats = getPortStats(portId);
    const isSelected = selectedPort === portId;

    const typeColors: Record<string, string> = {
      meter: 'border-blue-500 bg-blue-500/10',
      inverter: 'border-amber-500 bg-amber-500/10',
      hybrid: 'border-purple-500 bg-purple-500/10',
      unknown: 'border-slate-500 bg-slate-500/10',
    };

    const typeBadgeColors: Record<string, string> = {
      meter: 'bg-blue-500/20 text-blue-400',
      inverter: 'bg-amber-500/20 text-amber-400',
      hybrid: 'bg-purple-500/20 text-purple-400',
      unknown: 'bg-slate-500/20 text-slate-400',
    };

    return (
      <div
        key={portId}
        onClick={() => setSelectedPort(isSelected ? null : portId)}
        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
          isSelected ? typeColors[portType] : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-sm text-slate-300">{portId}</div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded ${typeBadgeColors[portType]}`}>
              {portType.toUpperCase()}
            </span>
            <button
              onClick={(e) => deletePort(portId, e)}
              className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
              title="Delete port"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        <div className="text-2xl font-bold text-white mb-2">
          {deviceCount} {deviceCount === 1 ? 'device' : 'devices'}
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-2 text-xs mt-3 pt-3 border-t border-slate-700">
            <div>
              <div className="text-slate-500">Requests</div>
              <div className="text-slate-300 font-mono">{stats.totalRequests}</div>
            </div>
            <div>
              <div className="text-slate-500">Avg Time</div>
              <div className="text-cyan-400 font-mono">{stats.avgResponseTime}ms</div>
            </div>
            <div>
              <div className="text-slate-500">Loss</div>
              <div className={`font-mono ${stats.packetLossPercent > 5 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.packetLossPercent}%
              </div>
            </div>
          </div>
        )}

        {isSelected && (
          <div className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-700">
            Click a device below to view details
          </div>
        )}
      </div>
    );
  };

  const renderDeviceCard = (portId: string, deviceIndex: string, device: DeviceInfo) => {
    const isSelected = selectedDevice?.portId === portId && selectedDevice?.deviceIndex === deviceIndex;

    // Calculate power percentage for inverters/hybrids
    let powerPercent = 0;
    let currentPower = 0;
    let ratedPower = device.ratedPower || 0;

    if (device.type === 'inverter' && !device.sleeping) {
      currentPower = device.data?.activePower || 0;
      powerPercent = ratedPower > 0 ? Math.min(100, (currentPower / ratedPower) * 100) : 0;
    } else if (device.type === 'hybrid' && !device.sleeping) {
      currentPower = device.data?.solarPower || device.data?.totalSolarPower || 0;
      powerPercent = ratedPower > 0 ? Math.min(100, (currentPower / ratedPower) * 100) : 0;
    }

    const showPowerBar = (device.type === 'inverter' || device.type === 'hybrid') && !device.sleeping;

    return (
      <div
        key={`${portId}-${deviceIndex}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedDevice(isSelected ? null : { portId, deviceIndex });
        }}
        className={`relative overflow-hidden rounded-lg border cursor-pointer transition-all ${
          isSelected
            ? 'border-cyan-500 bg-slate-800'
            : 'border-slate-600 bg-slate-800 hover:border-slate-500'
        }`}
      >
        {/* Power fill background */}
        {showPowerBar && (
          <div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-500/30 to-green-500/5 transition-all duration-500"
            style={{ height: `${powerPercent}%` }}
          />
        )}

        {/* Control limit line - always visible */}
        {showPowerBar && (
          <div
            className="absolute left-0 right-0 z-20 pointer-events-none"
            style={{ bottom: `${controlLimitPercent}%` }}
          >
            {/* Main limit line */}
            <div className={`h-0.5 w-full ${
              controlLimitPercent < 100
                ? 'bg-gradient-to-r from-red-500 via-red-400 to-red-500'
                : 'bg-gradient-to-r from-slate-500 via-slate-400 to-slate-500'
            }`} />
            {/* Limit percentage label */}
            <div className={`absolute -top-3 right-0.5 text-[9px] font-bold px-1 rounded ${
              controlLimitPercent < 100
                ? 'bg-red-500/90 text-white'
                : 'bg-slate-600/90 text-slate-200'
            }`}>
              {controlLimitPercent.toFixed(0)}%
            </div>
            {/* Left arrow indicator */}
            <div className={`absolute -top-1 -left-1 w-2 h-2 rotate-45 ${
              controlLimitPercent < 100 ? 'bg-red-500' : 'bg-slate-500'
            }`} />
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-500 font-mono">#{deviceIndex}</span>
            <div className="flex items-center gap-1">
              {device.sleeping && (
                <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">OFFLINE</span>
              )}
              {!device.sleeping && showPowerBar && (
                <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">
                  {powerPercent.toFixed(0)}%
                </span>
              )}
              <button
                onClick={(e) => deleteDevice(portId, deviceIndex, e)}
                className="p-0.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                title="Delete device"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="text-sm font-medium text-white truncate mb-2">
            {device.model || device.type}
          </div>

          {!device.sleeping && device.type === 'inverter' && (
            <div className="flex items-baseline justify-between">
              <span className="text-lg font-bold text-amber-400">
                {currentPower >= 1000 ? `${(currentPower / 1000).toFixed(1)}` : Math.round(currentPower)}
              </span>
              <span className="text-xs text-slate-500">
                {currentPower >= 1000 ? 'kW' : 'W'} / {ratedPower >= 1000 ? `${ratedPower / 1000}kW` : `${ratedPower}W`}
              </span>
            </div>
          )}

          {!device.sleeping && device.type === 'hybrid' && (
            <div className="space-y-1">
              <div className="flex items-baseline justify-between">
                <span className="text-lg font-bold text-purple-400">
                  {currentPower >= 1000 ? `${(currentPower / 1000).toFixed(1)}` : Math.round(currentPower)}
                </span>
                <span className="text-xs text-slate-500">
                  {currentPower >= 1000 ? 'kW' : 'W'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      (device.data?.batteryStateOfCharge || 0) < 20 ? 'bg-red-500' :
                      (device.data?.batteryStateOfCharge || 0) < 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${device.data?.batteryStateOfCharge || 0}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 font-mono">
                  {device.data?.batteryStateOfCharge || 0}%
                </span>
              </div>
            </div>
          )}

          {device.type === 'meter' && (
            <div className="flex items-baseline justify-between">
              <span className={`text-lg font-bold ${
                (device.data?.meterPower1 + device.data?.meterPower2 + device.data?.meterPower3 || 0) >= 0
                  ? 'text-green-400' : 'text-red-400'
              }`}>
                {Math.abs(Math.round(device.data?.meterPower1 + device.data?.meterPower2 + device.data?.meterPower3 || 0))}
              </span>
              <span className="text-xs text-slate-500">
                W {(device.data?.meterPower1 + device.data?.meterPower2 + device.data?.meterPower3 || 0) >= 0 ? 'export' : 'import'}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDeviceList = (portId: string, portData: PortData) => {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 mt-4">
        {Object.entries(portData).map(([deviceIndex, device]) =>
          renderDeviceCard(portId, deviceIndex, device)
        )}
      </div>
    );
  };

  const renderDeviceDetail = () => {
    if (!selectedDevice || !portsData) return null;

    const device = portsData[selectedDevice.portId]?.[selectedDevice.deviceIndex];
    if (!device) return null;

    const dataEntries = Object.entries(device.data || {}).filter(
      ([key, value]) => value !== null && value !== undefined
    );

    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {device.model || device.type} - #{selectedDevice.deviceIndex}
            </h3>
            <div className="text-sm text-slate-400">
              Port: {selectedDevice.portId}
            </div>
          </div>
          <button
            onClick={() => setSelectedDevice(null)}
            className="text-slate-400 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-slate-900 rounded p-3">
            <div className="text-xs text-slate-500">Type</div>
            <div className="text-sm text-white capitalize">{device.type}</div>
          </div>
          <div className="bg-slate-900 rounded p-3">
            <div className="text-xs text-slate-500">Rated Power</div>
            <div className="text-sm text-white">{device.ratedPower ? `${device.ratedPower} W` : '-'}</div>
          </div>
          <div className="bg-slate-900 rounded p-3">
            <div className="text-xs text-slate-500">Status</div>
            <div className={`text-sm ${device.sleeping ? 'text-red-400' : 'text-green-400'}`}>
              {device.sleeping ? 'Offline' : 'Online'}
            </div>
          </div>
          <div className="bg-slate-900 rounded p-3">
            <div className="text-xs text-slate-500">Last Update</div>
            <div className="text-sm text-cyan-400">{formatTimestamp(lastUpdate)}</div>
          </div>
        </div>

        <div className="border-t border-slate-700 pt-4">
          <h4 className="text-sm font-semibold text-slate-300 mb-3">Device Data</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {dataEntries.map(([key, value]) => (
              <div key={key} className="bg-slate-900 rounded p-2">
                <div className="text-xs text-slate-500 truncate" title={key}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
                <div className="text-sm text-white font-mono">
                  {formatValue(key, value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading device data...</p>
        </div>
      </div>
    );
  }

  const ports = portsData ? Object.entries(portsData) : [];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-slate-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-bold text-white">Advanced Info</h1>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-slate-400">
              <span className="text-slate-500">Updated:</span>{' '}
              <span className="text-white font-mono">{formatTimestamp(lastUpdate)}</span>
              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-mono ${
                timeSinceUpdate > 5 ? 'bg-yellow-500/20 text-yellow-400' :
                timeSinceUpdate > 2 ? 'bg-slate-700 text-slate-300' : 'bg-green-500/20 text-green-400'
              }`}>
                {formatTimeSince(timeSinceUpdate)}
              </span>
            </div>
            <div className="text-slate-400">
              <span className="text-white font-bold">{ports.length}</span> {ports.length === 1 ? 'port' : 'ports'}
            </div>
            {controlLimitPercent < 100 && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-orange-400 font-mono">{controlLimitPercent.toFixed(0)}% limit</span>
              </div>
            )}
            {/* Pause/Resume Button */}
            <button
              onClick={togglePause}
              className={`px-3 py-1.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                isPaused
                  ? 'bg-green-600 hover:bg-green-500 text-white'
                  : 'bg-red-600 hover:bg-red-500 text-white'
              }`}
            >
              {isPaused ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Resume
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                  Stop
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('devices')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'devices'
                  ? 'text-cyan-400 border-cyan-400'
                  : 'text-slate-400 border-transparent hover:text-slate-200'
              }`}
            >
              Devices
            </button>
            <button
              onClick={() => setActiveTab('coms')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'coms'
                  ? 'text-cyan-400 border-cyan-400'
                  : 'text-slate-400 border-transparent hover:text-slate-200'
              }`}
            >
              Coms Console
            </button>
            <button
              onClick={() => setActiveTab('control')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'control'
                  ? 'text-cyan-400 border-cyan-400'
                  : 'text-slate-400 border-transparent hover:text-slate-200'
              }`}
            >
              Control Debug
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <>
            {/* Port Grid */}
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">
                Communication Ports
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {ports.map(([portId, portData]) => renderPortCard(portId, portData))}
              </div>
            </div>

            {/* Expanded Port Devices */}
            {selectedPort && portsData?.[selectedPort] && (
              <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                <h3 className="text-sm font-semibold text-slate-300 mb-2">
                  Devices on {selectedPort}
                </h3>
                {renderDeviceList(selectedPort, portsData[selectedPort])}
              </div>
            )}

            {/* Device Detail */}
            {selectedDevice && renderDeviceDetail()}

            {/* Empty State */}
            {ports.length === 0 && (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-slate-400">No devices connected</p>
                <p className="text-sm text-slate-500 mt-2">
                  Run a scan from Settings to discover devices
                </p>
              </div>
            )}
          </>
        )}

        {/* Coms Console Tab */}
        {activeTab === 'coms' && (
          <ComsConsolePanel />
        )}

        {/* Control Debug Tab */}
        {activeTab === 'control' && (
          <ControlDebugPanel />
        )}
      </main>
    </div>
  );
}
